/**
 * Оболочка пошагового подбора (E07.1–E07.7).
 */

import { useEffect, useState } from "react";
import {
  PROJECT_ERROR_CODES,
  type CalcPreviewResult,
  type ClientAccessProject,
  type DialogueStatus,
  type ProjectStepType,
} from "@potok/shared";
import type { PublicCatalogApi } from "../../api/catalog.api";
import { ApiRequestError } from "../../api/http";
import { IndustryStep } from "./IndustryStep";
import { ModelsStep, type ModelSelection } from "./ModelsStep";
import { ParamsStep } from "./ParamsStep";
import { RegistrationForm } from "./RegistrationForm";
import { ResultStep } from "./ResultStep";
import { StatusStep } from "./StatusStep";
import { TourStep } from "./TourStep";
import { WizardProgress, toProgressStep } from "./WizardProgress";
import { readClientSession } from "../cabinet/client-session";

export type WizardProps = {
  catalogApi: PublicCatalogApi;
  restoredProject?: ClientAccessProject | null;
};

type WizardStep = "industry" | "models" | "status" | "tour" | "params" | "result" | "next";

export function Wizard({ catalogApi, restoredProject = null }: WizardProps) {
  const [step, setStep] = useState<WizardStep>("industry");
  const [projectId, setProjectId] = useState<string | null>(null);
  const [industryId, setIndustryId] = useState<string | null>(null);
  const [modelSelection, setModelSelection] = useState<ModelSelection[]>([]);
  const [dialogueStatus, setDialogueStatus] = useState<DialogueStatus | null>(null);
  const [modelIndex, setModelIndex] = useState(0);
  const [paramValues, setParamValues] = useState<Record<string, Record<string, string>>>({});
  const [results, setResults] = useState<Record<string, CalcPreviewResult>>({});
  const [persistError, setPersistError] = useState<string | null>(null);
  const [guestLimitReached, setGuestLimitReached] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);

  const currentModel = modelSelection[modelIndex] ?? null;
  const modelTotal = modelSelection.length;

  useEffect(() => {
    if (restoredProject === null) {
      return;
    }

    const industryStep = restoredProject.steps.find((step) => step.stepType === "industry");
    const modelsStep = restoredProject.steps.find((step) => step.stepType === "models");
    const statusStep = restoredProject.steps.find((step) => step.stepType === "status");
    const paramsSteps = restoredProject.steps.filter((step) => step.stepType === "params");
    const resultSteps = restoredProject.steps.filter((step) => step.stepType === "result");

    const restoredSelection = toModelSelection(modelsStep?.payload.selectedModels);
    const restoredParams: Record<string, Record<string, string>> = {};
    for (const step of paramsSteps) {
      if (typeof step.modelId === "string") {
        restoredParams[step.modelId] = toStringRecord(step.payload.values);
      }
    }

    const restoredResults: Record<string, CalcPreviewResult> = {};
    for (const step of resultSteps) {
      if (typeof step.modelId === "string" && isCalcPreviewResult(step.payload.result)) {
        restoredResults[step.modelId] = normalizeCalcPreviewResult(step.payload.result);
      }
    }

    setProjectId(restoredProject.id);
    setIndustryId(typeof industryStep?.payload.industryId === "string" ? industryStep.payload.industryId : null);
    setModelSelection(restoredSelection);
    setDialogueStatus(isDialogueStatus(statusStep?.payload.status) ? statusStep.payload.status : null);
    setParamValues(restoredParams);
    setResults(restoredResults);
    setPersistError(null);
    setGuestLimitReached(false);
    setShowRegistrationForm(false);

    const nextModelIndex = restoredSelection.findIndex((selection) => !restoredResults[selection.modelId]);
    if (restoredSelection.length === 0 || nextModelIndex === -1) {
      setModelIndex(Math.max(restoredSelection.length - 1, 0));
      setStep("next");
      return;
    }

    setModelIndex(nextModelIndex);
    setStep(restoredParams[restoredSelection[nextModelIndex]!.modelId] ? "result" : "params");
  }, [restoredProject]);

  useEffect(() => {
    if (restoredProject !== null) {
      return;
    }
    let cancelled = false;
    const session = readClientSession();
    void catalogApi
      .createProject({
        deviceToken: getOrCreateGuestDeviceToken(),
        ...(session?.personalCode ? { personalCode: session.personalCode } : {}),
      })
      .then((project) => {
        if (!cancelled) {
          setProjectId(project.id);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          if (error instanceof ApiRequestError && error.code === PROJECT_ERROR_CODES.GUEST_LIMIT_REACHED) {
            setGuestLimitReached(true);
            setPersistError(null);
            setStep("next");
            return;
          }
          setPersistError(describeError(error, "Не удалось создать проект"));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [catalogApi, restoredProject]);

  async function persistStep(
    stepType: ProjectStepType,
    payload: Record<string, unknown>,
    modelId: string | null = null,
  ): Promise<void> {
    if (projectId === null) {
      return;
    }
    try {
      await catalogApi.saveProjectStep(projectId, { stepType, modelId, payload });
      setPersistError(null);
    } catch (error: unknown) {
      setPersistError(describeError(error, "Не удалось сохранить шаг"));
    }
  }

  async function goAfterStatus(status: DialogueStatus | null = dialogueStatus): Promise<void> {
    if (status === null) {
      return;
    }
    await persistStep("status", { status });
    if (status === "peer" || status === "expert") {
      setStep("tour");
      return;
    }
    startModelCycle();
  }

  function startModelCycle(): void {
    if (modelSelection.length === 0) {
      setStep("next");
      return;
    }
    setModelIndex(0);
    setStep("params");
  }

  async function goToParamsOrNext(): Promise<void> {
    await persistStep("models", {
      selectedModels: modelSelection.map((item, index) => ({
        modelId: item.modelId,
        preferredUnitId: item.preferredUnitId,
        order: index + 1,
      })),
    });
    startModelCycle();
  }

  async function afterResult(result: CalcPreviewResult): Promise<void> {
    setResults((prev) => ({ ...prev, [result.modelId]: result }));
    await persistStep("result", { result }, result.modelId);
    const nextIndex = modelIndex + 1;
    if (nextIndex < modelSelection.length) {
      setModelIndex(nextIndex);
      setStep("params");
      return;
    }
    setStep("next");
  }

  if (step === "industry") {
    return (
      <div className="wizard">
        <WizardProgress current={toProgressStep(step)} />
        <IndustryStep
          api={catalogApi}
          selectedId={industryId}
          onSelect={setIndustryId}
          onContinue={() => {
            void persistStep("industry", { industryId });
            setStep("models");
          }}
        />
      </div>
    );
  }

  if (step === "models") {
    return (
      <div className="wizard">
        <WizardProgress current={toProgressStep(step)} />
        <ModelsStep
          api={catalogApi}
          industryId={industryId}
          selected={modelSelection}
          onChange={setModelSelection}
          onBack={() => setStep("industry")}
          onContinue={() => {
            void persistStep("models", {
              selectedModels: modelSelection.map((item, index) => ({
                modelId: item.modelId,
                preferredUnitId: item.preferredUnitId,
                order: index + 1,
              })),
            });
            setStep("status");
          }}
        />
      </div>
    );
  }

  if (step === "status") {
    return (
      <div className="wizard">
        <WizardProgress current={toProgressStep(step)} />
        <StatusStep
          api={catalogApi}
          industryId={industryId}
          selectedModels={modelSelection}
          status={dialogueStatus}
          onStatusChange={setDialogueStatus}
          onBack={() => setStep("models")}
          onContinue={(status) => {
            void goAfterStatus(status);
          }}
        />
      </div>
    );
  }

  if (step === "tour") {
    return (
      <div className="wizard">
        <WizardProgress current={toProgressStep(step)} />
        <TourStep
          api={catalogApi}
          industryId={industryId}
          onBack={() => setStep("status")}
          onContinue={() => {
            void goToParamsOrNext();
          }}
          onSkip={() => {
            void goToParamsOrNext();
          }}
        />
      </div>
    );
  }

  if (step === "params" && currentModel !== null) {
    return (
      <div className="wizard">
        <WizardProgress current={toProgressStep(step)} />
        <ParamsStep
          api={catalogApi}
          modelId={currentModel.modelId}
          modelLabel={
            modelTotal > 1
              ? `модель ${modelIndex + 1} из ${modelTotal}`
              : undefined
          }
          values={paramValues[currentModel.modelId] ?? {}}
          onChange={(values) =>
            setParamValues((prev) => ({ ...prev, [currentModel.modelId]: values }))
          }
          onBack={() => {
            if (modelIndex > 0) {
              setModelIndex(modelIndex - 1);
              setStep("result");
              return;
            }
            setStep(dialogueStatus === "peer" || dialogueStatus === "expert" ? "tour" : "status");
          }}
          onContinue={() => {
            void persistStep(
              "params",
              { values: paramValues[currentModel.modelId] ?? {} },
              currentModel.modelId,
            );
            setStep("result");
          }}
        />
      </div>
    );
  }

  if (step === "result" && currentModel !== null) {
    return (
      <div className="wizard">
        <WizardProgress current={toProgressStep(step)} />
        <ResultStep
          api={catalogApi}
          projectId={projectId}
          modelId={currentModel.modelId}
          unitId={currentModel.preferredUnitId}
          values={paramValues[currentModel.modelId] ?? {}}
          modelOrdinal={modelIndex + 1}
          modelTotal={modelTotal}
          onBack={() => setStep("params")}
          onContinue={(result) => {
            void afterResult(result);
          }}
        />
      </div>
    );
  }

  const completedCount = Object.keys(results).length;
  const completedResults = modelSelection
    .map((selection) => results[selection.modelId] ?? null)
    .filter((result): result is CalcPreviewResult => result !== null);
  const clientSession = readClientSession();
  const finalModel = modelSelection[modelSelection.length - 1] ?? null;

  function restartWizard(): void {
    setStep("industry");
    setIndustryId(null);
    setModelSelection([]);
    setDialogueStatus(null);
    setModelIndex(0);
    setParamValues({});
    setResults({});
    setPersistError(null);
    setGuestLimitReached(false);
    setShowRegistrationForm(false);
    setProjectId(null);
    const session = readClientSession();
    void catalogApi
      .createProject({
        deviceToken: getOrCreateGuestDeviceToken(),
        ...(session?.personalCode ? { personalCode: session.personalCode } : {}),
      })
      .then((project) => setProjectId(project.id))
      .catch((error: unknown) => {
        if (error instanceof ApiRequestError && error.code === PROJECT_ERROR_CODES.GUEST_LIMIT_REACHED) {
          setGuestLimitReached(true);
          setStep("next");
          return;
        }
        setPersistError(describeError(error, "Не удалось создать проект"));
      });
  }

  if (
    clientSession &&
    !guestLimitReached &&
    finalModel !== null &&
    completedResults.length > 0
  ) {
    return (
      <div className="wizard">
        <WizardProgress current={toProgressStep("next")} />
        <ResultStep
          api={catalogApi}
          projectId={projectId}
          modelId={finalModel.modelId}
          unitId={finalModel.preferredUnitId}
          values={paramValues[finalModel.modelId] ?? {}}
          modelOrdinal={modelSelection.length}
          modelTotal={modelTotal}
          layout="final"
          isRegisteredClient
          onBack={() => {
            setModelIndex(modelSelection.length - 1);
            setStep("result");
          }}
          onContinue={() => undefined}
          onRestart={restartWizard}
          onGoCabinet={() => {
            window.location.hash = "client-access";
          }}
        />
      </div>
    );
  }

  return (
    <div className="wizard">
      <WizardProgress current={toProgressStep("next")} />
      <section className="wizard-step" aria-labelledby="wizard-next-title">
      <h2 id="wizard-next-title">Зарегистрируйтесь, чтобы сохранить подбор</h2>
      <p className="wizard-step__hint">
        {guestLimitReached
          ? "Вы исчерпали лимит проходов гостя. Регистрация позволит продолжить подбор и сохранить историю."
          : completedCount > 0
            ? `Вы прошли ${completedCount} ${pluralizeResults(completedCount)}. Регистрация сохранит историю и откроет следующий уровень работы с проектами.`
            : "Регистрация сохранит историю подбора и позволит вернуться к проекту по персональному коду."}
      </p>
      {guestLimitReached ? (
        <p className="wizard-step__status wizard-step__status--error" role="alert">
          Лимит проходов гостя достигнут
        </p>
      ) : null}
      {persistError ? (
        <p className="wizard-step__status wizard-step__status--error" role="alert">
          {persistError}
        </p>
      ) : null}
      <aside className="register-offer" aria-labelledby="final-register-offer-title">
        <h3 id="final-register-offer-title">Что даст регистрация</h3>
        <ul>
          <li>История всех расчётов и возврат к проектам по 8-значному коду</li>
          <li>Доступ к прайсу, ведомости и дополнительным материалам после согласования</li>
          <li>Сохранение последних версий шагов по каждой выбранной модели</li>
        </ul>
        {completedResults.length > 0 ? (
          <>
            <h3 className="selection-summary__title">Готовые результаты</h3>
            <ol className="selection-summary__list">
              {completedResults.map((result, index) => (
                <li key={result.modelId}>
                  <span className="selection-summary__order" aria-hidden="true">
                    {index + 1}
                  </span>
                  <span className="selection-summary__label">Модель</span>
                  <span>
                    {result.modelName}
                    {result.unitCode ? ` · ${result.unitCode}` : ""}
                  </span>
                </li>
              ))}
            </ol>
          </>
        ) : null}
        <button
          type="button"
          className="wizard-step__primary"
          onClick={() => setShowRegistrationForm(true)}
        >
          Перейти к регистрации
        </button>
        {showRegistrationForm ? (
          <RegistrationForm
            api={catalogApi}
            projectId={projectId}
            onGoCabinet={() => {
              window.location.hash = "client-access";
            }}
            onGoWizard={() => setShowRegistrationForm(false)}
          />
        ) : null}
      </aside>
      <div className="wizard-step__actions">
        <button
          type="button"
          onClick={() => {
            if (modelSelection.length > 0) {
              setModelIndex(modelSelection.length - 1);
              setStep("result");
              return;
            }
            setStep("status");
          }}
        >
          Назад
        </button>
      </div>
    </section>
    </div>
  );
}

const GUEST_DEVICE_TOKEN_KEY = "potok_guest_device_token";

function getOrCreateGuestDeviceToken(): string {
  try {
    const existing = localStorage.getItem(GUEST_DEVICE_TOKEN_KEY);
    if (existing && existing.length > 0) {
      return existing;
    }

    const token =
      globalThis.crypto && "randomUUID" in globalThis.crypto
        ? globalThis.crypto.randomUUID()
        : `guest-${Math.random().toString(16).slice(2)}-${Date.now().toString(16)}`;

    localStorage.setItem(GUEST_DEVICE_TOKEN_KEY, token);
    return token;
  } catch {
    // Если localStorage недоступен (например, приватный режим), лимит будет неидеален,
    // но wizard продолжит работу.
    return `guest-${Date.now()}`;
  }
}

function describeError(error: unknown, fallback: string): string {
  if (error instanceof ApiRequestError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
}

function pluralizeResults(count: number): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) {
    return "модель";
  }
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
    return "модели";
  }
  return "моделей";
}

function toModelSelection(value: unknown): ModelSelection[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => {
      if (typeof item !== "object" || item === null) {
        return null;
      }
      const row = item as {
        modelId?: unknown;
        preferredUnitId?: unknown;
        order?: unknown;
      };
      if (typeof row.modelId !== "string") {
        return null;
      }
      return {
        modelId: row.modelId,
        preferredUnitId: typeof row.preferredUnitId === "string" ? row.preferredUnitId : null,
        order: typeof row.order === "number" ? row.order : Number.MAX_SAFE_INTEGER,
      };
    })
    .filter((item): item is ModelSelection & { order: number } => item !== null)
    .sort((left, right) => left.order - right.order)
    .map(({ order: _order, ...selection }) => selection);
}

function toStringRecord(value: unknown): Record<string, string> {
  if (typeof value !== "object" || value === null) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value).filter(
      (entry): entry is [string, string] => typeof entry[0] === "string" && typeof entry[1] === "string",
    ),
  );
}

function isCalcPreviewResult(value: unknown): value is CalcPreviewResult {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as { modelId?: unknown; modelName?: unknown; outputs?: unknown };
  return typeof row.modelId === "string" && typeof row.modelName === "string" && Array.isArray(row.outputs);
}

function normalizeCalcPreviewResult(value: CalcPreviewResult): CalcPreviewResult {
  return {
    ...value,
    restrictedOutputs: value.restrictedOutputs ?? [],
  };
}

function isDialogueStatus(value: unknown): value is DialogueStatus {
  return value === "beginner" || value === "peer" || value === "expert";
}
